import { format, utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import { addHours } from 'date-fns';
import timezones from '@/assets/json/timezones.json';
import { DEFAULT_TIMEZONES, COMMON_TIMEZONES } from '@/common/config';
import groupBy from 'lodash/groupBy';

const defaultLocalTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

// One offset formatter per zone: a calendar labels every tile through getUtcOffset, and Intl construction dominates the cost.
const offsetFormatters = new Map();
const offsetFormatter = (timeZone) => {
    if (!offsetFormatters.has(timeZone)) offsetFormatters.set(timeZone, new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'longOffset' }));
    return offsetFormatters.get(timeZone);
};

// The region groups PHP's DateTimeZone::listIdentifiers() returns, which is what the med pass
// accepts (LocationTimeZone::usable). Intl alone also takes the legacy 'MST', 'EST5EDT' and
// 'US/Mountain', which the server ignores - a zone shown here that doses do not run on.
const IANA_REGION = /^(Africa|America|Antarctica|Arctic|Asia|Atlantic|Australia|Europe|Indian|Pacific)\//;

/**
 * Whether a stored timeZone is one the server will schedule against.
 *
 * @param {*} zone
 * @returns {boolean}
 */
export const isUsableTimeZone = (zone) => {
    if (typeof zone !== 'string' || (zone !== 'UTC' && !IANA_REGION.test(zone))) return false;
    try {
        new Intl.DateTimeFormat('en-US', { timeZone: zone });
        return true;
    } catch {
        return false;
    }
};

const timezone = {
    // TODO: add validation to the following methods
    format: function (dateString, stringFormat, timeZone = defaultLocalTimeZone) {
        if (!stringFormat) stringFormat = 'yyyy-MM-dd';
        return format(dateString, stringFormat, { timeZone: timeZone });
    },
    getLocalDateTime: function (utcDateString, stringFormat, localTimeZone = defaultLocalTimeZone) {
        return format(utcToZonedTime(utcDateString, localTimeZone), stringFormat || "yyyy-MM-dd'T'HH:mm:ss.SSSxxx", { timeZone: localTimeZone });
    },
    getGlobalDateTime: function (localDateString, stringFormat, localTimeZone = defaultLocalTimeZone) {
        return format(utcToZonedTime(zonedTimeToUtc(new Date(localDateString), localTimeZone), 'UTC'), stringFormat || "yyyy-MM-dd'T'HH:mm:ss.SSSxxx", { timeZone: 'UTC' });
    },
    convertToTimezone: function (dateString, stringFormat, fromTimeZone, toTimeZone) {
        const utc = timezone.getGlobalDateTime(dateString, "yyyy-MM-dd'T'HH:mm:ss.SSSxxx", fromTimeZone);
        return timezone.getLocalDateTime(utc, stringFormat, toTimeZone);
    },
    getTimezoneData: function (timezone) {
        return timezones.find((zone) => zone.timezone === timezone || zone.abbr === timezone || zone.abbrDST === timezone);
    },
    getLocalTimeZone: function () {
        return defaultLocalTimeZone;
    },

    // Preserve curated labels for listed zones and use Intl for browser-supported zones absent from timezones.json.
    getTimezoneAbbreviation: function (timeZone, dateString) {
        const date = dateString || new Date().toString();
        const zone = timezone.getTimezoneData(timeZone);
        if (zone) return timezone.isZoneDST(timeZone, date) ? zone.abbrDST : zone.abbr;
        try {
            return new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'short' }).formatToParts(new Date(date)).find((part) => part.type === 'timeZoneName')?.value ?? '';
        } catch {
            return '';
        }
    },

    // The zone's offset from UTC in milliseconds at the moment (NaN for a zone or date Intl rejects); two zones keeping the
    // same clock share it. Read from Intl at the instant: date-fns-tz getTimezoneOffset takes the Date as the zone's wall-clock
    // time, which lands on the wrong side of a DST change for the few hours before it.
    getUtcOffset: function (timeZone, dateString) {
        try {
            const name = offsetFormatter(timeZone)
                .formatToParts(new Date(dateString || Date.now()))
                .find((part) => part.type === 'timeZoneName')?.value;
            // A zero offset may be named plain 'GMT' rather than 'GMT+00:00', depending on the runtime's ICU.
            if (name === 'GMT') return 0;
            const match = /^GMT([+-])(\d{2}):(\d{2})$/.exec(name ?? '');
            if (!match) return NaN;
            return (match[1] === '-' ? -1 : 1) * (Number(match[2]) * 60 + Number(match[3])) * 60000;
        } catch {
            return NaN;
        }
    },

    // The generic name ('MT', not 'MST'/'MDT'): a schedule's times hold on both sides of a DST
    // change, so the label does too. A zone with no DST keeps its one name ('America/Phoenix' -> 'MST').
    getTimezoneShortName: function (timeZone) {
        if (!timeZone || typeof timeZone !== 'string') return timeZone;
        try {
            const part = new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'shortGeneric' }).formatToParts(new Date()).find((item) => item.type === 'timeZoneName');
            return part?.value || timeZone;
        } catch {
            return timeZone;
        }
    },

    // Pass the moment being displayed (e.g. a signature's `when`) so the standard/daylight label matches it.
    getLocalTimeZoneAbbreviation: function (dateString) {
        return timezone.getTimezoneAbbreviation(timezone.getLocalTimeZone(), dateString);
    },

    setGlobalTimeToDST: function (dateString) {
        // set timezone of browser (user creating event)
        const baseTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        // determine if baseTimeZone uses DST
        const DST = timezone.getTimezoneData(baseTimeZone)?.isdst;
        // is the dateString currently in DST
        const isDST = timezone.isDST(dateString);
        // if the baseTimeZone (user creating the event) is in a DST timezone AND they are not currently in DST mode then add 1 hour to the event.
        if (!isDST && DST) dateString = timezone.getGlobalDateTime(addHours(new Date(dateString), 1), "yyyy-MM-dd'T'HH:mm:ss.SSSxxx");
        // or return formatted dateString
        else dateString = timezone.getGlobalDateTime(new Date(dateString), "yyyy-MM-dd'T'HH:mm:ss.SSSxxx");

        return dateString;
    },

    // Whether the zone is on daylight time at the moment: its offset above its standard, the smaller of its Jan 1 / Jul 1 offsets.
    isZoneDST: function (timeZone, dateString) {
        const date = new Date(dateString);
        const year = date.getFullYear();
        const standard = Math.min(timezone.getUtcOffset(timeZone, new Date(year, 0, 1)), timezone.getUtcOffset(timeZone, new Date(year, 6, 1)));
        return timezone.getUtcOffset(timeZone, date) > standard;
    },

    // Whether the browser's own zone is on daylight time at the moment.
    isDST: function (dateString) {
        return timezone.isZoneDST(timezone.getLocalTimeZone(), dateString);
    },

    loadPickerByRegion: function (region) {
        if (!region) region = DEFAULT_TIMEZONES;
        else if (region === 'all') region = null;
        // region can be either a string or array:  'America' or ['America', 'Pacific']
        let results = COMMON_TIMEZONES;
        const sourceData = region ? timezones.filter((zone) => region.includes(zone.region)) : timezones;
        for (const [key, value] of Object.entries(groupBy(sourceData, 'region'))) {
            if (results.find((item) => item.header === key)) continue;
            results.push({ header: key });
            value.forEach((zone) => {
                results.push({ timezone: zone.timezone });
            });
        }
        return results;
    },
    timeToMilitary: function (time) {
        // currently accepts time in this format:  12:00 am, 1:00 PM, 11:29 AM, etc.
        // this works for the current purpose, but we may want to extend it to allow for no space between mm and ampm.
        let hh, mm, ampm;
        [hh, mm, ampm] = time.split(/:| /);
        hh = Number(hh);
        if (ampm?.toLowerCase() === 'pm' && hh < 12) hh += 12;
        else if (ampm?.toLowerCase() === 'am' && hh === 12) hh -= 12;
        return hh.toString().padStart(2, '0') + ':' + mm;
    },

    /**
     * This is only here as a code reference. This is what I created to build assets/json/timezones.json
     * If we need to update the json we can run this again.
     */
    // buildTimezoneJson: async function () {
    //     let data = [];
    //     await axios.get('http://worldtimeapi.org/api/timezone').then((source) => {
    //         source.data.forEach((item) => {
    //             axios.get('http://worldtimeapi.org/api/timezone/' + item).then((response) => {
    //                 const tz = response.data;
    //                 const zone = tz.timezone.split('/');
    //                 let record = {};
    //                 record.name = zone[zone.length - 1].replace('_', ' ');
    //                 record.region = zone[0];
    //                 record.timezone = tz.timezone;
    //                 record.DST = tz.dst_offset > 0;
    //                 record.abbr = record.DST ? tz.abbreviation.replace('ST', 'T').replace('DT', 'ST') : tz.abbreviation;
    //                 record.abbrDST = tz.abbreviation;
    //                 record.utcOffset = tz.raw_offset;
    //                 record.utcOffsetDST = record.DST ? tz.raw_offset + 3600 : tz.raw_offset;
    //                 data.push(record);
    //             });
    //         });
    //     });
    //     return data;
    // },
};
export default timezone;
